<template>
  <div class="payment-card-offer">
    <AmTypography
      class="title text--dark-3"
      :text="$t('containerLeft.cardOffer.specialOffer')"
      typography-style="l1"
      weight="bold"
    />
    <div class="order-bump-warning">
      <span class="order-bump-message">{{ $t('containerLeft.cardOffer.orderBumpMessage') }}</span>
    </div>
    <div
      :key="upSell.id"
      class="product"
    >
      <div class="product__icon">
        <img
          width="80"
          height="80"
          :src="imageBundleOrProductLoading(upSell)"
          alt="package"
          loading="lazy"
        />
      </div>
      <div class="product__details">
        <AmTypography
          class="product__details__name text--dark-3"
          typography-style="l5"
        >
          <template #default>
            {{ upSell.name }}
          </template>
        </AmTypography>
        <AmTypography
          class="product__details__price"
          typography-style="l5"
        >
          <template>
            {{ filterApplierOldPrice(upSell) }}
          </template>
        </AmTypography>
        <AmTypography
          class="product__details__price-offer text--blue"
          typography-style="l5"
        >
          <template>
            {{ filterApplierCurrentPrice(upSell) }}
          </template>
        </AmTypography>
      </div>
    </div>
    <AmButton
      :class="[`purchase-finish ${theme.color}`]"
      :label="finishPurchaseText"
      color="blue"
      size="xl"
      full-width
      :icon="iconAddShoppingCart"
      :disabled="loadingCart"
      @click="showModalUpsellConfirmation = true"
    />

    <AmModal
      v-if="showModalUpsellConfirmation"
      :title="$t('containerLeft.cardOffer.specialOffer')"
      @close="showModalUpsellConfirmation = false"
    >
      <div v-if="loading">
        <PaymentCardLoading />
      </div>
      <div v-else>
        <template v-if="status === modals.offer">
          <AmTypography
            class="title text--dark-3"
            :text="$t('containerLeft.cardOffer.crossSellConfirmation')"
            typography-style="l5"
          />
          <AmTypography
            class="title text--dark-3"
            typography-style="l5"
            weight="bold"
          >
            {{ displayCreditCardInfo() }}
          </AmTypography>
          <AmButton
            :class="[`purchase-finish ${theme.color}`]"
            :label="$t('containerLeft.cardOffer.purchaseConfirmation')"
            color="blue"
            size="xl"
            full-width
            :icon="iconAddShoppingCart"
            :disabled="loadingCart"
            @click="postUpsell()"
          />
        </template>
        <SuccessfulPurchase
          v-if="status === modals.success"
          :installmentSelectedLabel="getInstallmentSelectedLabel"
          :paymentType="getPaymentType"
          :upsell="upSell"
        />
        <div
          v-if="status === modals.error"
          class="error"
        >
          <div class="error-title">
            <AmIconMaterial
              class="error__icon text--red"
              name="warning"
              size="md"
            />
            <AmTypography
              :text="$t('steps.payment.paymentProcessError.title')"
              text-color="error"
              typography-style="l6"
              weight="bold"
            />
          </div>
          <div class="error-message">
            <AmTypography
              :text="$t('steps.payment.paymentProcessError.subTitle')"
              text-color="error"
              typography-style="l6"
              weight="bold"
            />
          </div>
        </div>
      </div>
    </AmModal>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import themeMixin from '@/components/mixins/themeMixin'
import currencyFilterMixin from '@/components/mixins/currencyFilterMixin'
import loadImageBundlesMixin from '@/components/mixins/loadImageBundlesMixin'
import SuccessfulPurchase from '@/views/Checkout/partials/SuccessfulPurchase/SuccessfulPurchase.vue'
import PaymentCardLoading from '@/views/Checkout/partials/steps/Payment/payments/PaymentCard/PaymentCardLoading/PaymentCardLoading.vue'

export default {
  name: 'PaymentCardOfferUpSell',
  components: {
    SuccessfulPurchase,
    PaymentCardLoading,
  },
  mixins: [themeMixin, currencyFilterMixin, loadImageBundlesMixin],
  props: {
    creditCardFlag: {
      type: String(),
      default: '',
    },
    creditCardLastDigits: {
      type: String(),
      default: '0000',
    },
    paymentType: {
      type: String,
    },
    installmentSelectedLabel: {
      type: [String, Object],
    },
  },
  data() {
    return {
      iconAddShoppingCart: {
        name: 'add_shopping_cart',
        position: 'left',
        type: 'material',
        size: 'xl',
      },
      showModalUpsellConfirmation: false,
      showUpsellSummary: false,
      showModalError: false,
      loading: false,
      status: 'OFFER',
      modals: {
        offer: 'OFFER',
        success: 'SUCCESS',
        error: 'ERROR',
      },
    }
  },
  computed: {
    ...mapState('Checkout', {
      cart: (state) => state.cart,
      state: (state) => state,
      loadingCart: (state) => state.cartLoading,
    }),
    ...mapGetters('Cart', {
      upSell: 'getFirstUpsell',
    }),
    getInstallmentSelectedLabel() {
      return this.installmentSelectedLabel
    },
    getPaymentType() {
      return this.paymentType
    },
    finishPurchaseText() {
      return this.$t('containerRigth.requests.purchase.buttonLabel')
    },
  },
  methods: {
    ...mapActions('Cart', ['createUpsell']),
    filterApplierOldPrice(upSell) {
      return this.$t('containerLeft.cardOffer.oldPrice', {
        price: this.$options.filters.currencyBRL(upSell?.settings?.oldPrice),
      })
    },
    displayCreditCardInfo() {
      return this.$t('containerLeft.cardOffer.creditCardConfirmation', {
        cardFlag: this.creditCardFlag,
        lastDigits: this.creditCardLastDigits,
      })
    },
    filterApplierCurrentPrice(upSell) {
      return this.$t('containerLeft.cardOffer.currentPrice', {
        price: this.$options.filters.currencyBRL(upSell.price),
      })
    },
    showLoading(value) {
      this.loading = value
    },
    async postUpsell() {
      this.showLoading(true)
      try {
        const { data } = await this.createUpsell(this.upSell.id)
        this.upSell.value = data.value
        this.upSell.quantity = data.installments
        this.status = this.modals.success
      } catch (error) {
        this.status = this.modals.error
      } finally {
        this.showLoading(false)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.payment-card-offer {
  .add-cart,
  .purchase-finish {
    margin-top: 15px;
  }

  .title {
    margin: 32px auto 24px auto;
    text-align: center;
    flex-direction: column;
  }
  .card-details {
    display: inline-block;
  }
  .product {
    display: flex;
    align-items: center;
    margin: 15px 0 0 0;
    @include mobile {
      margin: 15px 0 30px 0 !important;
    }
    padding: 10px;
    border: solid $dark-40 1px;
    border-radius: 0.5em;

    &__details {
      &__name,
      &__price,
      &__price-offer {
        margin-left: 16px;
        span {
          font-weight: 500;
        }
      }

      &__price {
        text-decoration: line-through;
        text-decoration-color: $dark-40;
        margin-top: 6px;
      }

      &__price-offer {
        margin-top: 3px;
      }
    }
  }

  .offer {
    display: flex;
    background: rgba($error, 0.8);
    justify-content: center;
    align-items: center;
    padding: 12px 0 12px 0;

    ::v-deep div,
    span {
      color: $white;
    }

    &__timer {
      margin-right: 11px;
    }
  }

  .quantity-offer {
    display: flex;
    justify-content: space-between;
    margin-top: 25px;
    user-select: none;

    &__navigations {
      cursor: pointer;
    }
  }

  .return {
    cursor: pointer;
    margin-top: 24px;
    text-align: center;
  }

  .order-bump-warning {
    background-color: #e46f61;
    display: flex;
    justify-content: center;
    align-items: center;
    color: #ffffff;
    font-size: 16px;
    height: 40px;
  }

  .order-bump-message {
    margin-right: 10px;
  }
}

.error {
  display: flex;
  flex-direction: column;
  margin: 30px 0;

  .error-title {
    display: flex;
    justify-content: center;
  }

  .error-message {
    text-align: center;
  }
}

::v-deep {
  @include mobile {
    .am-modal-wrapper {
      .am-modal-container {
        width: 90% !important;
      }
    }
  }
  #am-modal-close {
    cursor: pointer;
  }
}

::v-deep {
  .modal-loading {
    #am-modal-close {
      display: none !important;
    }
  }
}
</style>
